import type {
  ConsignmentContractDetail,
  UserContractInfo,
} from '@/entities/contract/api/consignment-contract.api';
import {
  isContractDocumentFinalizedStatus,
  isContractDocumentSigningReviewStatus,
  isPendingReviewDisplayStatus,
  isRecontractRequestedDisplayStatus,
  isSigningReviewDisplayStatus,
  normalizeContractDisplayStatus,
  getContractDisplayStatusLabel,
  isContractCompletedDisplayStatus,
  hasFinalizedContractDocument,
  CONTRACT_DISPLAY_STATUS,
} from '@/pages/users/contractDisplayStatus';
import { formatDateTime } from '@/shared/lib/dateFormat';

export const CONSIGNMENT_ADMIN_REVIEW_STATUS = {
  BEFORE_CHECK: 'BEFORE_CHECK',
  RECONTRACT_REQUEST: 'RECONTRACT_REQUEST',
  COMPLETE: 'COMPLETE',
} as const;

export type ConsignmentAdminReviewStatus =
  (typeof CONSIGNMENT_ADMIN_REVIEW_STATUS)[keyof typeof CONSIGNMENT_ADMIN_REVIEW_STATUS];

export const CONSIGNMENT_ADMIN_REVIEW_STATUS_OPTIONS = [
  { label: '확인 전', value: CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK },
  {
    label: '재계약 요청',
    value: CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST,
  },
  { label: '계약 완료', value: CONSIGNMENT_ADMIN_REVIEW_STATUS.COMPLETE },
] as const;

export const CONSIGNMENT_RECONTRACT_REASON_OPTIONS = [
  { label: '서명 이미지 문제', value: 'SIGNATURE_IMAGE_ISSUE' },
  { label: '계약 내용 변경', value: 'CONTRACT_CONTENT_CHANGE' },
  { label: '사업자 정보 변경', value: 'BUSINESS_INFORMATION_CHANGE' },
] as const;

export const API_CONTRACT_REVIEW_STATUS = {
  PENDING_REVIEW: 'PENDING_REVIEW',
  RECONTRACT_REQUESTED: 'RECONTRACT_REQUESTED',
  CONTRACT_COMPLETED: 'CONTRACT_COMPLETED',
} as const;

export const mapAdminReviewStatusToApi = (
  uiStatus: ConsignmentAdminReviewStatus | null | undefined,
): string | null => {
  switch (uiStatus) {
    case CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK:
      return API_CONTRACT_REVIEW_STATUS.PENDING_REVIEW;
    case CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST:
      return API_CONTRACT_REVIEW_STATUS.RECONTRACT_REQUESTED;
    case CONSIGNMENT_ADMIN_REVIEW_STATUS.COMPLETE:
      return API_CONTRACT_REVIEW_STATUS.CONTRACT_COMPLETED;
    default:
      return null;
  }
};

export const mapApiReviewStatusToAdmin = (
  apiStatus: unknown,
): ConsignmentAdminReviewStatus | null => {
  const key = String(apiStatus ?? '').toUpperCase();
  if (key === API_CONTRACT_REVIEW_STATUS.PENDING_REVIEW) {
    return CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK;
  }
  if (key === API_CONTRACT_REVIEW_STATUS.RECONTRACT_REQUESTED) {
    return CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST;
  }
  if (key === API_CONTRACT_REVIEW_STATUS.CONTRACT_COMPLETED) {
    return CONSIGNMENT_ADMIN_REVIEW_STATUS.COMPLETE;
  }
  return null;
};

export const resolveContractIdFromInfo = (
  contractInfo: UserContractInfo | null | undefined,
  pendingRow?: { contractId?: string | number; id?: string | number } | null,
): string | null => {
  const fromInfo =
    contractInfo?.contractId ?? contractInfo?.contractID ?? contractInfo?.id;
  if (fromInfo != null && String(fromInfo).trim() !== '') {
    return String(fromInfo).trim();
  }
  const fromPending = pendingRow?.contractId ?? pendingRow?.id ?? null;
  if (fromPending != null && String(fromPending).trim() !== '') {
    return String(fromPending).trim();
  }
  return null;
};

export const hasReplacementContractStarted = (
  contractDetail: ConsignmentContractDetail | null | undefined,
): boolean => {
  if (!contractDetail) return false;
  const replacementId =
    contractDetail.replacementContractId ?? contractDetail.replacementContractID;
  return replacementId != null && String(replacementId).trim() !== '';
};

export const getContractVersionChainFromDetail = (
  detail: ConsignmentContractDetail | null | undefined,
) => {
  if (!detail) return null;
  const contractVersion = detail.contractVersion ?? detail.contract_version;
  const previousContractId =
    detail.previousContractId ?? detail.previousContractID;
  const replacementContractId =
    detail.replacementContractId ?? detail.replacementContractID;

  if (
    contractVersion == null &&
    previousContractId == null &&
    replacementContractId == null
  ) {
    return null;
  }

  return { contractVersion, previousContractId, replacementContractId };
};

export const getContractSignedAtFromDetail = (
  detail: ConsignmentContractDetail | null | undefined,
): string | null => {
  if (!detail) return null;
  return (
    detail.contractDocument?.signedAt ??
    detail.signedAt ??
    detail.contractDocument?.signed_at ??
    null
  );
};

export const getSignedHtmlDownloadUrlFromContractDetail = (
  detail: ConsignmentContractDetail | null | undefined,
): string | null => {
  if (!detail) return null;
  const doc = detail.contractDocument;
  return (
    detail.signedHtmlDownloadUrl ??
    detail.signedHtmlDownloadURL ??
    doc?.signedHtmlDownloadUrl ??
    doc?.signedHtmlDownloadURL ??
    null
  );
};

export const getSignedHtmlInlineUrlFromContractDetail = (
  detail: ConsignmentContractDetail | null | undefined,
): string | null => {
  if (!detail) return null;
  const doc = detail.contractDocument;
  return (
    detail.signedHtmlPresignedUrl ??
    detail.signedHtmlPresignedURL ??
    detail.signedHtmlPreviewUrl ??
    detail.signedHtmlPreviewURL ??
    detail.signedHtmlUrl ??
    detail.presignedSignedHtmlUrl ??
    detail.signedHtmlPresignedUrlForAdmin ??
    doc?.signedHtmlPresignedUrl ??
    doc?.signedHtmlPresignedURL ??
    doc?.signedHtmlPreviewUrl ??
    doc?.signedHtmlPreviewURL ??
    null
  );
};

export const getSignedHtmlContentFromContractDetail = (
  detail: ConsignmentContractDetail | null | undefined,
): string | null => {
  if (!detail) return null;
  const doc = detail.contractDocument;
  const raw =
    detail.signedHtmlContent ??
    detail.signedHtml ??
    doc?.signedHtmlContent ??
    doc?.signedHtml ??
    null;
  if (typeof raw === 'string' && raw.trim()) return raw;
  return null;
};

/**
 * 서명 HTML 미리보기가 실제로 필요할 때만 signed-html API를 친다.
 * 탭이 숨겨져 있거나, 상세에 HTML/URL이 이미 있으면 호출하지 않는다.
 */
export const shouldFetchContractSignedHtml = ({
  active,
  isTabLoading,
  isReviewPending,
  contractId,
  contractDetail,
}: {
  active: boolean;
  isTabLoading: boolean;
  isReviewPending: boolean;
  contractId?: string | null;
  contractDetail?: ConsignmentContractDetail | null;
}): boolean => {
  if (!active || isTabLoading || !isReviewPending) return false;
  if (!contractId?.trim()) return false;
  if (getSignedHtmlContentFromContractDetail(contractDetail)) return false;
  if (getSignedHtmlInlineUrlFromContractDetail(contractDetail)) return false;
  if (getSignedHtmlDownloadUrlFromContractDetail(contractDetail)) return false;
  return true;
};

export const isContractDocumentFinalized = (
  contractDetail: ConsignmentContractDetail | null | undefined,
  contractInfo: UserContractInfo | null | undefined,
): boolean =>
  isContractDocumentFinalizedStatus(
    contractDetail?.status ?? contractInfo?.status,
  );

export const isContractDocumentSigningReview = (
  contractDetail: ConsignmentContractDetail | null | undefined,
  contractInfo: UserContractInfo | null | undefined,
): boolean =>
  isContractDocumentSigningReviewStatus(
    contractDetail?.status ?? contractInfo?.status,
  );

export const isConsignmentContractReviewPending = (
  contractInfo: UserContractInfo | null | undefined,
  contractDetail: ConsignmentContractDetail | null | undefined,
): boolean => {
  if (!contractInfo && !contractDetail) return false;

  const candidates = [
    {
      displayStatus: contractInfo?.contractStatus,
      reviewStatus: contractInfo?.reviewStatus,
      documentStatus: contractInfo?.status,
    },
    {
      displayStatus: contractDetail?.contractStatus,
      reviewStatus: contractDetail?.reviewStatus,
      documentStatus: contractDetail?.status,
    },
    {
      displayStatus: contractInfo?.status,
      reviewStatus: null,
      documentStatus: contractInfo?.status,
    },
  ];

  return candidates.some((candidate) =>
    isSigningReviewDisplayStatus(
      candidate.displayStatus,
      candidate.reviewStatus,
      candidate.documentStatus,
    ),
  );
};

export const isConsignmentReviewStatusPending = (
  contractInfo: UserContractInfo | null | undefined,
  contractDetail: ConsignmentContractDetail | null | undefined,
): boolean => {
  const reviewStatus =
    contractDetail?.reviewStatus ?? contractInfo?.reviewStatus ?? null;
  const displayStatus = contractInfo?.contractStatus ?? null;
  return isPendingReviewDisplayStatus(displayStatus, reviewStatus);
};

export const isConsignmentRecontractReviewActive = (
  contractInfo: UserContractInfo | null | undefined,
  contractDetail: ConsignmentContractDetail | null | undefined,
): boolean => {
  const reviewStatus =
    contractDetail?.reviewStatus ?? contractInfo?.reviewStatus ?? null;
  const displayStatus = contractInfo?.contractStatus ?? null;
  return isRecontractRequestedDisplayStatus(displayStatus, reviewStatus);
};

/** 계약 완료 상태 — 검토 상태 변경 폼 노출 (레거시 userStatusTag) */
export const isConsignmentContractCompletedReviewActive = (
  contractInfo: UserContractInfo | null | undefined,
  contractDetail: ConsignmentContractDetail | null | undefined,
): boolean => {
  if (contractInfo == null && contractDetail == null) return false;
  const reviewStatus =
    contractDetail?.reviewStatus ?? contractInfo?.reviewStatus ?? null;
  const displayStatus = contractInfo?.contractStatus ?? null;
  if (isContractCompletedDisplayStatus(displayStatus, reviewStatus)) {
    return true;
  }
  return hasFinalizedContractDocument(
    displayStatus,
    reviewStatus,
    contractDetail?.status ?? contractInfo?.status,
  );
};

export const shouldShowConsignmentAdminReviewForm = (
  contractInfo: UserContractInfo | null | undefined,
  contractDetail: ConsignmentContractDetail | null | undefined,
): boolean =>
  isConsignmentContractReviewPending(contractInfo, contractDetail) ||
  isConsignmentReviewStatusPending(contractInfo, contractDetail) ||
  isConsignmentRecontractReviewActive(contractInfo, contractDetail) ||
  isConsignmentContractCompletedReviewActive(contractInfo, contractDetail);

/** 레거시 getContractFinalPdfDownloadUrlFromDetail */
export const getContractFinalPdfDownloadUrlFromDetail = (
  detail: ConsignmentContractDetail | null | undefined,
  contractInfo?: UserContractInfo | null,
): string | null => {
  const doc = detail?.contractDocument;
  const url =
    detail?.finalPdfDownloadUrl ??
    detail?.finalPdfDownloadURL ??
    doc?.finalPdfDownloadUrl ??
    doc?.finalPdfDownloadURL ??
    contractInfo?.finalPdfDownloadUrl ??
    contractInfo?.finalPdfDownloadURL ??
    contractInfo?.originalFileUrl ??
    null;
  if (url == null || String(url).trim() === '') return null;
  return String(url).trim();
};

export const buildConsignmentAdminReviewStatusSelectOptions = ({
  replacementStarted,
  currentReviewKey,
}: {
  replacementStarted: boolean;
  currentReviewKey: string | null | undefined;
}) =>
  CONSIGNMENT_ADMIN_REVIEW_STATUS_OPTIONS.map((option) => ({
    ...option,
    disabled:
      option.value === CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK ||
      (replacementStarted &&
        option.value === CONSIGNMENT_ADMIN_REVIEW_STATUS.COMPLETE) ||
      (replacementStarted &&
        currentReviewKey === API_CONTRACT_REVIEW_STATUS.RECONTRACT_REQUESTED &&
        option.value !== CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST),
  }));

export const isExternalContractFromInfo = (
  contractInfo: UserContractInfo | null | undefined,
): boolean => {
  if (!contractInfo) return false;
  return (
    normalizeContractDisplayStatus(contractInfo.contractStatus) ===
    CONTRACT_DISPLAY_STATUS.EXTERNAL_CONTRACT
  );
};

/**
 * 계약서 다운로드/미리보기 가능 여부.
 * 외부 계약 유형이어도 contractInfo의 문서 ID·URL이 있으면 노출한다.
 * pending 목록에서만 찾은 DRAFT ID는 쓰지 않는다.
 */
export const hasConsignmentContractDownloadSource = (
  contractInfo: UserContractInfo | null | undefined,
  finalPdfDownloadUrl?: string | null,
): boolean => {
  if (resolveContractIdFromInfo(contractInfo)) return true;
  const url =
    finalPdfDownloadUrl ??
    contractInfo?.finalPdfDownloadUrl ??
    contractInfo?.finalPdfDownloadURL ??
    contractInfo?.originalFileUrl;
  return url != null && String(url).trim() !== '';
};

export const formatContractDateTimeDisplay = formatDateTime;

export const getContractStatusLabel = (
  contractInfo: UserContractInfo | null | undefined,
): string => {
  if (!contractInfo) {
    return getContractDisplayStatusLabel(CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED);
  }
  const rawStatus = String(contractInfo.contractStatus ?? '').trim();
  const statusKey =
    normalizeContractDisplayStatus(rawStatus) ??
    (isContractCompletedDisplayStatus(rawStatus, contractInfo.reviewStatus)
      ? CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED
      : null);
  if (statusKey) return getContractDisplayStatusLabel(statusKey);
  return getContractDisplayStatusLabel(rawStatus);
};

export const isContractCompletedFromInfo = (
  contractInfo: UserContractInfo | null | undefined,
): boolean => {
  if (!contractInfo) return false;
  return hasFinalizedContractDocument(
    contractInfo.contractStatus,
    contractInfo.reviewStatus,
    contractInfo.status,
  );
};
